/**
 * 桶码 → 展示描述（P1-1.7，"怎么画"的一半；"怎么说"在 ./copy.js）
 *
 * 真源是 metaspace `api_reference/UserHealthHabitInsightApi/enums.md` 那张 28 码表
 * （Java 侧由 `HabitBucket` 枚举 + `HabitBucketCodeTableTest` 双向钉住）。
 * 本文件的三张码表（有模板 / 显式忽略 / 未知）合起来必须覆盖那 28 个码 —— 这条由
 * `buildChecks.test.js` 在 CI 上卡住（feasibility §7 P1 验收「registry.js 对桶码清单覆盖率 100%」）。
 *
 * **顺序不在这里决定**：服务端已按层的优先级排好并保证同层只留第一个命中，
 * 前端再排一次就是第二份判定逻辑（D12）。
 *
 * tone 只影响视觉重量，不改变语义：warning=需要知道的口径问题，info=一句话说明，muted=这一维现在没内容。
 * 不许出现"优秀/良好/偏差"这类评价词 —— 服务端只给桶码，评价是文案层擅自加就会变成定性结论（D3/D11）。
 */

/** 层号只用于「显式忽略」时写清为什么忽略，不参与渲染排序。 */
export const BUCKET_LAYERS = {
  PROTECTION: '① 失真/风险保护',
  SUFFICIENCY: '② 数据充分性',
  TARGET_PATH: '③ 目标关键路径',
  OUTCOME: '④ 达成/形态',
  FALLBACK: '⑥ 兜底',
};

/** code → 展示描述。新增码必须先进 enums.md 再进这里，反过来说明流程倒了。 */
export const BUCKET_NOTICES = {
  // 层①：数据质量
  'common.dataStale': {
    tone: 'warning',
    title: '数据更新滞后',
    copyKey: 'dataStale',
    layer: 'PROTECTION',
  },
  'common.noData': {
    tone: 'muted',
    title: '没有可观测数据',
    copyKey: 'noData',
    layer: 'SUFFICIENCY',
  },
  'common.lowEvidence': {
    tone: 'info',
    title: '整窗证据不足',
    copyKey: 'lowEvidence',
    layer: 'SUFFICIENCY',
  },
  // 层②：维内充分性
  'sport.insufficient': {
    tone: 'info',
    title: '运动记录还不够',
    copyKey: 'dimInsufficient',
    layer: 'SUFFICIENCY',
  },
  'sleep.insufficient': {
    tone: 'info',
    title: '睡眠数据还不够',
    copyKey: 'dimInsufficient',
    layer: 'SUFFICIENCY',
  },
  'diet.insufficient': {
    tone: 'info',
    title: '饮食记录还不够',
    copyKey: 'dimInsufficient',
    layer: 'SUFFICIENCY',
  },
  'mental.insufficient': {
    tone: 'info',
    title: '心理记录还不够',
    copyKey: 'dimInsufficient',
    layer: 'SUFFICIENCY',
  },
  // 层③：目标口径（P1 只有运动维有自设目标）
  'sport.noSelfTarget': {
    tone: 'info',
    title: '还没有自设目标',
    copyKey: 'noSelfTarget',
    layer: 'TARGET_PATH',
  },
  'sport.targetIsDefault': {
    tone: 'info',
    title: '目标是系统默认值',
    copyKey: 'targetIsDefault',
    layer: 'TARGET_PATH',
  },
};

/**
 * 已登记但本期**故意不画**的码：enums.md 里标 P2 的 19 个。
 *
 * 登记（而不是让它们落进"未知码"）有两个作用：
 * 1. 覆盖率 100% 这条验收项是可证的 —— "每个码要么有模板、要么显式登记忽略"；
 * 2. 服务端提前发出 P2 码时，页面行为与"没这个码"完全一致，但埋点里能分清
 *    「前端已知而没画」与「前端根本没见过」，前者是排期问题、后者是契约漂移。
 */
export const BUCKET_IGNORED = {
  'sport.noDeviceSync': 'P2',
  'sport.loadSpike': 'P2',
  'diet.parsePoor': 'P2',
  'sport.stableAchiever': 'P2',
  'sport.rising': 'P2',
  'sport.falling': 'P2',
  'sport.streakLong': 'P2',
  'sport.pref.uniform': 'P2',
  'sport.pref.insufficient': 'P2',
  'sport.nightActivity': 'P2',
  'diet.recordOnly': 'P2',
  'diet.structured': 'P2',
  'sleep.deviceBound': 'P2',
  'sleep.stagesAvailable': 'P2',
  'mental.behaviorOnly': 'P2',
  'sport.defaultView': 'P2',
  'sleep.defaultView': 'P2',
  'diet.defaultView': 'P2',
  'mental.defaultView': 'P2',
};

export const KNOWN_BUCKET_CODES = Object.keys(BUCKET_NOTICES);
export const REGISTERED_BUCKET_CODES = Object.keys(BUCKET_NOTICES).concat(
  Object.keys(BUCKET_IGNORED),
);

/**
 * 桶码归一：有模板的画提示条，没模板的（含已登记忽略的）交给埋点。
 * 未知码**不降级、不崩页**：服务端先上新码、前端后发是常态（两次发布不可能同时），
 * 把它们当错误会让每次加码变成事故。
 */
export function noticeFor(code) {
  return BUCKET_NOTICES[code] || null;
}

export function isRegisteredBucket(code) {
  return (
    Object.prototype.hasOwnProperty.call(BUCKET_IGNORED, code) ||
    Object.prototype.hasOwnProperty.call(BUCKET_NOTICES, code)
  );
}

/** 维度展示名：这是表现层的事，不进契约。 */
export const DIMENSION_LABEL = {
  SPORT: '运动',
  SLEEP: '睡眠',
  DIET: '饮食',
  MENTAL: '心理',
};

/**
 * 每维主指标 —— `features.mean/p25/p50/p75`、`baseline.*` 与日历格用的是同一个量。
 *
 * **载荷里没有这个字段名**：服务端只给数字，量纲是前端贴的标签，所以这里错一个维度，
 * 页面就会把"分钟"写成"小时"。三个数（序列键 / 单位 / 汇总口径）必须同一处维护，
 * `registry.test.js` 用五份真样例反查 `aggregate`：
 *  · `sum` ⇒ 该维 `facts[factKey].value / features.mean` 必须是 ≤ activeDays 的整数（对得上"总时长 ÷ 有信号天数 = 均值"）；
 *  · `mean` ⇒ 该 fact 的值必须等于 `features.mean`（睡眠维的 sleepHoursAvg 就是均值本身）；
 *  · `none` ⇒ 该维没有可反查的 fact（饮食的热量没有事实行，enums.md facts 表可核对）。
 * 反查失配 = 服务端换了主指标 ⇒ 这张表要跟着改，测试会先红。
 */
export const PRIMARY_METRIC = {
  SPORT: {
    label: '训练时长',
    unit: '分钟',
    series: 'sport.activeDuration',
    factKey: 'activeMinutes',
    aggregate: 'sum',
  },
  SLEEP: {
    label: '睡眠时长',
    unit: '小时',
    series: 'sleep.sleepHours',
    factKey: 'sleepHoursAvg',
    aggregate: 'mean',
  },
  DIET: {
    label: '摄入热量',
    unit: '千卡',
    series: 'diet.dietTakenKilocalories',
    factKey: null,
    aggregate: 'none',
  },
  MENTAL: {
    label: '心理记录时长',
    unit: '分钟',
    series: 'mental.mentalRecordDuration',
    factKey: 'mentalMinutes',
    aggregate: 'sum',
  },
};

/**
 * 卡片的“主数字”取哪一条事实行（2026-09-16 拍板的可读性调整）。
 *
 * 为什么不用主指标的合计：饮食的“窗口内共 25,044 千卡”没人读得懂，而“记录了 48 次”一眼就知道习惯。
 * 所以这里取“最能代表这一维习惯”的事实行；事实行缺失时由卡片退回到主指标合计，不会空白。
 * 数组是按优先级排的候选，取第一个有值的。
 */
export const HERO_FACT = {
  SPORT: ['activeTimes', 'activeMinutes'],
  SLEEP: ['sleepHoursAvg'],
  DIET: ['dietRecords'],
  MENTAL: ['mentalRecords', 'mentalMinutes'],
};

/**
 * 每维的**全部**日粒度指标（P1-1.7 表现层扩展）。
 *
 * 为什么要有这张表：`PRIMARY_METRIC` 只够画"一维一格"，而 indicators 一次就返回了该维的全部
 * 序列（运动时长/次数/步数、饮食热量/蛋白/脂肪/碳水/记录次数、心理时长/次数、睡眠时长/心率…）。
 * 逐日矩阵的每一行都指向这里的一项 —— 想多画一条指标只需在此加一行，不用改渲染。
 *
 * 每项四个字段：
 *  · `series` 取 indicators 的路径（`维小写.驼峰指标`）；
 *  · `label` / `unit` ：**单位必须与序列同一处维护**（贴错就是把分钟写成小时，页面上看不出来）；
 *  · `aggregate` ：窗口汇总口径 —— `sum` 合计（次数/时长/热量/步数）、`mean` 均值（睡眠时长）、
 *    `latest` 最近一次（心率类）。"睡眠时长求和"没有意义，所以口径不能全省成 sum。
 *
 * 主指标那一项直接从 `PRIMARY_METRIC` 摊开，不重写一遍 label/unit（两处写一份必然漂移）。
 * 是否主指标由 `series` 相等判定，不在数据里存冗余标志。
 */
export const DIMENSION_METRICS = {
  SPORT: [
    { ...PRIMARY_METRIC.SPORT, key: 'activeDuration' },
    {
      key: 'activeTimes',
      series: 'sport.activeTimes',
      label: '训练次数',
      unit: '次',
      aggregate: 'sum',
    },
    {
      key: 'steps',
      series: 'sport.steps',
      label: '步数',
      unit: '步',
      aggregate: 'sum',
    },
    {
      key: 'activeKilocalories',
      series: 'sport.activeKilocalories',
      label: '活动消耗',
      unit: '千卡',
      aggregate: 'sum',
    },
  ],
  SLEEP: [
    { ...PRIMARY_METRIC.SLEEP, key: 'sleepHours' },
    {
      key: 'rhr',
      series: 'sleep.rhr',
      label: '静息心率',
      unit: 'bpm',
      aggregate: 'latest',
    },
    {
      key: 'hrv',
      series: 'sleep.hrv',
      label: '心率变异性',
      unit: 'ms',
      aggregate: 'latest',
    },
  ],
  DIET: [
    { ...PRIMARY_METRIC.DIET, key: 'dietTakenKilocalories' },
    {
      key: 'dietTakenProtein',
      series: 'diet.dietTakenProtein',
      label: '蛋白质',
      unit: '克',
      aggregate: 'sum',
      kcalPerGram: 4,
    },
    {
      key: 'dietTakenFat',
      series: 'diet.dietTakenFat',
      label: '脂肪',
      unit: '克',
      aggregate: 'sum',
      kcalPerGram: 9,
    },
    {
      key: 'dietTakenCho',
      series: 'diet.dietTakenCho',
      label: '碳水化合物',
      unit: '克',
      aggregate: 'sum',
      kcalPerGram: 4,
    },
    {
      key: 'dietRecordTimes',
      series: 'diet.dietRecordTimes',
      label: '记录次数',
      unit: '次',
      aggregate: 'sum',
    },
  ],
  MENTAL: [
    { ...PRIMARY_METRIC.MENTAL, key: 'mentalRecordDuration' },
    {
      key: 'mentalRecordTimes',
      series: 'mental.mentalRecordTimes',
      label: '记录次数',
      unit: '次',
      aggregate: 'sum',
    },
  ],
};

/** 服务端"这一维没有可观测数据"的桶码。用它判断"要不要画图"，而不是前端自己数天数（D12）。 */
export const NO_DATA_BUCKET = 'common.noData';

/**
 * 这一维是不是"窗口内一个真值都没有"。
 * 口径由服务端下发的桶码决定：前端数一遍 `observed` 也能得到同样结论，但那是第二份判据。
 */
export function isDimensionEmpty(dim) {
  const buckets = Array.isArray(dim && dim.buckets) ? dim.buckets : [];
  return buckets.includes(NO_DATA_BUCKET);
}

/**
 * `features.weeklyAttainment[i]` 的量纲**不是**主指标量纲：服务端按"该周次数之和"汇总，
 * 睡眠维没有独立次数列 ⇒ 退化为小时数之和（metaspace `HabitProfileComposer#featureInput`）。
 * 所以周柱的单位只能按维取，不能复用 PRIMARY_METRIC.unit，否则睡眠周柱会被标成"次"。
 */
export const WEEK_UNIT = {
  SPORT: '次',
  DIET: '次',
  MENTAL: '次',
  SLEEP: '小时',
};

/**
 * `stage` → 本期画哪些块（§5.5 的"解锁内容/明确不给"两列的前端落点）。
 *
 * 这里只做 `stage` 字符串 → 布尔的映射，**不看天数**：档位是服务端判的（决策 ④），
 * 前端一旦写 `nDays >= 14` 就有第二份判据（R16 拦这条，buildChecks 会扫）。
 *
 * 分数与合成分**不在这里门控**：`score.observed` / `index.observed` 已经是服务端给的三态，
 * 再套一层 stage 开关会把"真算出来的分"藏掉。stage 只门控"这个档位该不该出现这块内容"。
 */
export const STAGE_SECTIONS = {
  // 还没有可观测数据：只给定义卡 + 空日历轨，一个数字都不出现
  S0: {
    progress: true,
    weekly: false,
    weeklyWeeks: 0,
    calendar: true,
    facts: false,
    definition: true,
  },
  // 刚开始记录：逐日事实 + 进度，不给指数/趋势/时段偏好
  S1: {
    progress: true,
    weekly: false,
    weeklyWeeks: 0,
    calendar: true,
    facts: true,
    definition: false,
  },
  // 少量记录：周槽（未覆盖的槽本来就是灰的）+ 时段次数 + 临时分（临时分由 score.provisional 决定）
  S2: {
    progress: true,
    weekly: true,
    weeklyWeeks: 6,
    calendar: true,
    facts: true,
    definition: false,
  },
  // 有一定记录：只讲 4 周，6 周趋势这时候还不能出现（§5.5 S3 的"明确不给"）
  S3: {
    progress: false,
    weekly: true,
    weeklyWeeks: 4,
    calendar: true,
    facts: true,
    definition: false,
  },
  S4: {
    progress: false,
    weekly: true,
    weeklyWeeks: 6,
    calendar: true,
    facts: true,
    definition: false,
  },
  S5: {
    progress: false,
    weekly: true,
    weeklyWeeks: 6,
    calendar: true,
    facts: true,
    definition: false,
  },
};

/** 卡片渲染顺序：服务端 dimensions 是对象，键序不可依赖 ⇒ 前端固定一个顺序（表现层决定，不是判定）。 */
export const DIMENSION_ORDER = ['SPORT', 'SLEEP', 'DIET', 'MENTAL'];

/** 时段偏好四段（features.active*Times）的展示标签与取值键。
 * `label` 带小时区间（卡片/图例用）、`short` 只给结论句用（“运动时间集中在傍晚”）。
 */
export const PREF_SLOTS = [
  { key: 'activeMorningTimes', label: '早 06–12', short: '早晨' },
  { key: 'activeAfternoonTimes', label: '午 12–18', short: '下午' },
  { key: 'activeEveningTimes', label: '晚 18–23', short: '傍晚' },
  { key: 'activeNightTimes', label: '夜 23–06', short: '夜间' },
];

/**
 * 三大营养素（饮食卡片里的“供能占比”）。
 *
 * 从 `DIMENSION_METRICS.DIET` 里过滤出带 `kcalPerGram` 的三项，**不重写一遍 label/series**
 * （两处写一份必然漂移，这是本仓反复强调的）。
 *
 * 为什么用“供能占比”而不是“克数占比”：脂肪每克 9 千卡，蛋白质/碳水只有 4，按克数画饼
 * 会把脂肪画得很小，读者会读成“脂肪吃得少”——那是个错误的直观。
 * 换算系数就是 Atwater 系数（4/9/4 千卡每克），已在 method.js 的 CITATIONS 里登记出处。
 */
export const MACRO_METRICS = DIMENSION_METRICS.DIET.filter(
  (item) => item.kcalPerGram > 0,
);

/**
 * 每个档位**看得见哪几张卡**（D12 硬约定：不够就不出现）。
 *
 * 为什么这张表跟 STAGE_SECTIONS 并列而不是写在 index.jsx 里：两者是同一个约定的两半 ——
 * 一个管"块画不画"、一个管"卡出不出现"，分两处写迟早漂移。S1 只给运动卡就是这条：
 * 睡眠不够时给一张空的睡眠卡，比不给更伤（半张图 = 半真半假的结论）。
 * S0 反而是四张全给：那一期的产物就是"这些天里能看到什么"，空卡本身就是结论。
 */
export const STAGE_VISIBLE_DIMS = {
  S0: ['SPORT', 'SLEEP', 'DIET', 'MENTAL'],
  S1: ['SPORT'],
  S2: ['SPORT', 'SLEEP'],
  S3: ['SPORT', 'SLEEP', 'DIET', 'MENTAL'],
  S4: ['SPORT', 'SLEEP', 'DIET', 'MENTAL'],
  S5: ['SPORT', 'SLEEP', 'DIET', 'MENTAL'],
};

/**
 * 整页级码（`common.*`）：跨维口径，只在页顶说一次。
 * 不滤的话 brand-new 会把同一句“整窗证据不足”说五遍（页顶一次 + 四维各一次），
 * 而卡片里那一遍没有信息量，只把卡片堆高到看不见图。
 * 纯前缀字符串判断，不是判据（D12）。
 *
 * `common.noData` 是例外：它带 `common.` 前缀，说的却是**某一维**的事（服务端把它下在某维的
 * buckets 里）。提到页顶就成了一句没有主语的"这一维…"，运营看不出是哪一维 ——
 * 它跟维度走，由趋势区在那一维的组里说（那里有维度名当主语）。
 */
export const PAGE_LEVEL_BUCKET_PREFIX = 'common.';

export function isPageLevelBucket(code) {
  if (code === NO_DATA_BUCKET) return false;
  return typeof code === 'string' && code.startsWith(PAGE_LEVEL_BUCKET_PREFIX);
}

/**
 * 阶段档位（S0–S5）的页面上层说法。数字含义见 enums.md，这里不复述判据。
 *
 * ⚠ 这六句只描述**数据覆盖到什么程度**，不描述这个人习不习惯：
 * "较稳定/已养成"那类词是稳定性结论，而稳定性判据在服务端桶码里（P2 才上），
 * 前端用档位名抢着说就是 R16 违例（§5.5 S3 的"明确不给"包括它）。
 */
export const STAGE_HEADLINE = {
  S0: '还没有可观测数据',
  S1: '刚开始记录',
  S2: '有少量记录',
  S3: '有一定记录',
  S4: '记录覆盖较完整',
  S5: '记录覆盖整窗',
};

/**
 * 合成分缺失时页面该说什么。注意：缺失是**正常形态**（决策 ⑤：只有一维够格时不给合成分），
 * 不是错误，所以这里用中性陈述而不是告警色。
 */
export const INDEX_ABSENT_COPY = {
  SINGLE: '当前只有一个维度够格出分，暂不合成总指数。',
  INSUFFICIENT: '各维度记录量还不足以合成总指数。',
  PARTIAL: '部分维度够格，总指数按实际参与维度计算。',
  FULL: '四个维度均参与合成。',
};
